import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Eye, Trash, Plus, WarningCircle, CheckCircle, Clock, DownloadSimple } from "@phosphor-icons/react";

/**
 * Documents page.
 *
 * Upload workflow (per BSVAC spec):
 *   • Every upload starts in PENDING APPROVAL.
 *   • Categories in `expirationRequired` show an inline Expiration Date
 *     field on the row — expiration lives on the document, not on the
 *     member profile.
 *   • The dialog supports adding multiple documents in one session — a
 *     single "Submit for Approval" click sends them all as a batch and
 *     the backend fans a SINGLE consolidated notification to admins.
 *
 * Review workflow:
 *   • Admins/Supervisors see APPROVE / REJECT buttons on pending rows.
 *   • Approving stamps the reviewer's name + timestamp on the doc and
 *     flips the status; only Approved docs count toward compliance and
 *     drive expiration alerts.
 */

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_ICON = {
  pending: Clock,
  approved: CheckCircle,
  rejected: WarningCircle,
};

function fmtExp(d) {
  if (!d) return null;
  const t = new Date(d + "T12:00:00").getTime();
  const days = Math.round((t - Date.now()) / 86400000);
  const label = new Date(d + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  let cls = "text-slate-500";
  let tag = `${days}d`;
  if (days < 0) { cls = "text-red-700 font-bold"; tag = `expired ${Math.abs(days)}d ago`; }
  else if (days < 30) cls = "text-amber-700";
  return <span className={`mono text-[11px] ${cls}`}>{label} · {tag}</span>;
}

/** Inline expiration-date cell. Click the date (or the "+ Add" link
 *  when none is set) to edit; a tiny date input appears next to a Save
 *  button. iter35 — members can self-service their own cert expiration
 *  dates without re-uploading the file. Supervisors can edit anyone's. */
function InlineExpiration({ doc, canEdit, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(doc.expiration_date || "");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setVal(doc.expiration_date || ""); }, [doc.expiration_date]);
  if (!canEdit) {
    return fmtExp(doc.expiration_date) || <span className="text-slate-400 text-xs">—</span>;
  }
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 hover:underline cursor-pointer"
        data-testid={`docs-exp-edit-${doc.id}`}
        title="Click to edit expiration date"
      >
        {fmtExp(doc.expiration_date) || <span className="text-slate-500 text-xs underline-offset-2 underline">+ Add date</span>}
      </button>
    );
  }
  async function save() {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("expiration_date", val || "");
      const { data } = await api.patch(`/documents/${doc.id}`, fd);
      toast.success("Expiration date updated");
      setEditing(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex items-center gap-1" data-testid={`docs-exp-edit-row-${doc.id}`}>
      <Input
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="rounded-sm h-7 w-36 text-xs"
        data-testid={`docs-exp-input-${doc.id}`}
      />
      <Button size="sm" onClick={save} disabled={busy} className="rounded-sm h-7 px-2 text-xs bg-slate-900 text-white" data-testid={`docs-exp-save-${doc.id}`}>
        {busy ? "…" : "Save"}
      </Button>
      <Button size="sm" variant="outline" onClick={() => { setEditing(false); setVal(doc.expiration_date || ""); }} className="rounded-sm h-7 px-2 text-xs" data-testid={`docs-exp-cancel-${doc.id}`}>
        Cancel
      </Button>
    </span>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isSup = isSupervisorOrAbove(user);
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [expReq, setExpReq] = useState([]);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([{ category: "", file: null, expiration_date: "", certification_number: "", dl_number: "", dl_state: "" }]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);

  async function load() {
    const [d, c, e] = await Promise.all([
      api.get("/documents/"),
      api.get("/documents/categories"),
      api.get("/documents/expiration-required"),
    ]);
    setDocs(d.data);
    setCats(c.data);
    setExpReq(e.data);
  }
  useEffect(() => { load(); }, []);

  // Build a fast lookup: does a raw category label require an expiration
  // date? Matches on canonical label + every alias returned by the API.
  const categoryRequiresExp = useMemo(() => {
    const s = new Set();
    for (const entry of expReq) {
      s.add(entry.canonical.toLowerCase());
      for (const a of entry.aliases || []) s.add(a.toLowerCase());
    }
    return (cat) => s.has(String(cat || "").trim().toLowerCase());
  }, [expReq]);

  // Per-category extra fields: EMT Cert asks for a Cert Number,
  // Driver's License asks for DL Number + State. Sourced from the same
  // /documents/expiration-required endpoint so backend + frontend stay
  // in sync — no separate config drift possible.
  const extraFieldsFor = useMemo(() => {
    const map = {};
    for (const entry of expReq) {
      for (const [lbl, f] of Object.entries(entry.extra_fields_by_label || {})) map[lbl] = new Set(f);
    }
    return (cat) => map[String(cat || "").trim().toLowerCase()] || new Set();
  }, [expReq]);

  const US_STATES_LIST = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
  ];

  function reset() {
    setRows([{ category: cats[0] || "", file: null, expiration_date: "", certification_number: "", dl_number: "", dl_state: "" }]);
  }
  function addRow() {
    if (rows.length >= 10) return;
    setRows([...rows, { category: cats[0] || "", file: null, expiration_date: "", certification_number: "", dl_number: "", dl_state: "" }]);
  }
  function removeRow(i) {
    if (rows.length === 1) return;
    setRows(rows.filter((_, j) => j !== i));
  }
  function updateRow(i, patch) {
    setRows(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  }

  async function submitBatch(e) {
    e.preventDefault();
    // Basic validation up front so the user isn't hit with a partial
    // upload on the server.
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.file) { toast.error(`Row ${i + 1}: pick a file`); return; }
      if (!r.category) { toast.error(`Row ${i + 1}: pick a category`); return; }
      if (categoryRequiresExp(r.category) && !r.expiration_date) {
        toast.error(`Row ${i + 1}: '${r.category}' requires an expiration date`); return;
      }
      const extras = extraFieldsFor(r.category);
      if (extras.has("certification_number") && !r.certification_number) {
        toast.error(`Row ${i + 1}: '${r.category}' requires a Certification Number`); return;
      }
      if (extras.has("dl_number") && !r.dl_number) {
        toast.error(`Row ${i + 1}: '${r.category}' requires a Driver License Number`); return;
      }
      if (extras.has("dl_state") && !r.dl_state) {
        toast.error(`Row ${i + 1}: '${r.category}' requires a State of Issue`); return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      for (const r of rows) {
        fd.append("files", r.file);
        fd.append("categories", r.category);
        fd.append("expiration_dates", r.expiration_date || "");
        // Always send the parallel extra-field arrays so the backend's
        // per-index indexing lines up cleanly. Empty strings are ignored
        // server-side unless the category actually needs them.
        fd.append("certification_numbers", r.certification_number || "");
        fd.append("dl_numbers", r.dl_number || "");
        fd.append("dl_states", r.dl_state || "");
      }
      const { data } = await api.post("/documents/batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.uploaded} document${data.uploaded === 1 ? "" : "s"} submitted for approval`);
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  async function review(doc, status) {
    const fd = new FormData();
    fd.append("status", status);
    fd.append("reviewer_note", "");
    await api.post(`/documents/${doc.id}/review`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(status === "approved" ? "Approved" : "Rejected");
    load();
  }
  async function remove(doc) {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/documents/${doc.id}`);
    load();
  }

  const filtered = docs.filter((d) => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return d.filename.toLowerCase().includes(f) || d.category.toLowerCase().includes(f) || (d.user_name || "").toLowerCase().includes(f);
  });

  const pendingCount = docs.filter((d) => d.status === "pending").length;

  return (
    <AppLayout
      title="Documents"
      subtitle={isSup ? "Member documents — review and approve" : "Your uploaded documents"}
      actions={
        <div className="flex items-center gap-2">
          {isSup && pendingCount > 0 && (
            <Badge className="rounded-sm bg-amber-500 text-white" data-testid="docs-pending-count">
              {pendingCount} pending
            </Badge>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-sm h-9 px-2 text-xs bg-white"
            data-testid="docs-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending only</option>
            <option value="approved">Approved only</option>
            <option value="rejected">Rejected only</option>
          </select>
          <Input placeholder="Search…" className="rounded-sm w-56" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="docs-search" />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-slate-900 text-white" data-testid="docs-upload">
                <UploadSimple size={14} weight="bold" className="mr-1" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader className="pb-2 border-b border-slate-200">
                <DialogTitle className="font-display text-xl">Upload Documents</DialogTitle>
                <p className="text-xs text-slate-500 mt-1">Every document waits for an administrator's review before it counts toward compliance.</p>
              </DialogHeader>
              <form onSubmit={submitBatch} className="space-y-3 pt-3">
                {rows.map((r, i) => {
                  const needsExp = categoryRequiresExp(r.category);
                  const extras = extraFieldsFor(r.category);
                  return (
                    <div key={i} className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2" data-testid={`docs-row-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="tab-tag text-slate-500">Document {i + 1}</div>
                        {rows.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="text-red-600 h-7 w-7" onClick={() => removeRow(i)} data-testid={`docs-del-row-${i}`}>
                            <Trash size={13} />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="tab-tag">Category</Label>
                          <select
                            value={r.category}
                            onChange={(e) => updateRow(i, {
                              category: e.target.value,
                              expiration_date: "", certification_number: "", dl_number: "", dl_state: "",
                            })}
                            className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white"
                            data-testid={`docs-cat-${i}`}
                          >
                            <option value="">Select category…</option>
                            {cats.map((c) => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="tab-tag">File (PDF, JPG, PNG, DOC · 10MB max)</Label>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png"
                            onChange={(e) => updateRow(i, { file: e.target.files?.[0] || null })}
                            className="rounded-sm bg-white"
                            data-testid={`docs-file-${i}`}
                          />
                        </div>
                      </div>
                      {needsExp && (
                        <div className="space-y-1" data-testid={`docs-exp-wrap-${i}`}>
                          <Label className="tab-tag text-amber-700">Expiration Date — required for {r.category}</Label>
                          <Input
                            type="date"
                            required
                            value={r.expiration_date}
                            onChange={(e) => updateRow(i, { expiration_date: e.target.value })}
                            className="rounded-sm bg-white max-w-xs"
                            data-testid={`docs-exp-${i}`}
                          />
                        </div>
                      )}
                      {extras.has("certification_number") && (
                        <div className="space-y-1" data-testid={`docs-certnum-wrap-${i}`}>
                          <Label className="tab-tag text-amber-700">EMT Certification Number</Label>
                          <Input
                            required
                            value={r.certification_number}
                            onChange={(e) => updateRow(i, { certification_number: e.target.value })}
                            className="rounded-sm bg-white max-w-sm mono"
                            placeholder="e.g. NY-EMT-12345"
                            data-testid={`docs-certnum-${i}`}
                          />
                        </div>
                      )}
                      {(extras.has("dl_number") || extras.has("dl_state")) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`docs-dl-wrap-${i}`}>
                          {extras.has("dl_number") && (
                            <div className="space-y-1">
                              <Label className="tab-tag text-amber-700">Driver License Number</Label>
                              <Input
                                required
                                value={r.dl_number}
                                onChange={(e) => updateRow(i, { dl_number: e.target.value })}
                                className="rounded-sm bg-white mono"
                                placeholder="e.g. D123-4567-8901"
                                data-testid={`docs-dlnum-${i}`}
                              />
                            </div>
                          )}
                          {extras.has("dl_state") && (
                            <div className="space-y-1">
                              <Label className="tab-tag text-amber-700">State of Issue</Label>
                              <select
                                required
                                value={r.dl_state}
                                onChange={(e) => updateRow(i, { dl_state: e.target.value })}
                                className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white"
                                data-testid={`docs-dlstate-${i}`}
                              >
                                <option value="">Select state…</option>
                                {US_STATES_LIST.map((s) => <option key={s}>{s}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm"
                    onClick={addRow}
                    disabled={rows.length >= 10}
                    data-testid="docs-addrow"
                  >
                    <Plus size={12} weight="bold" className="mr-1" /> Add another document
                  </Button>
                  <div className="text-[10px] text-slate-500">Up to 10 per submission · a single notification is sent to admins</div>
                </div>
                <DialogFooter className="pt-3 border-t border-slate-200">
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={uploading} className="rounded-sm bg-slate-900 text-white" data-testid="docs-submit">
                    {uploading ? "Submitting…" : "Submit for Approval"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              {isSup && <th className="px-4 py-2">Member</th>}
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">File</th>
              <th className="px-4 py-2">Expiration</th>
              <th className="px-4 py-2">Uploaded</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isSup ? 7 : 6} className="px-4 py-6 text-center text-slate-500">No documents.</td></tr>
            ) : filtered.map((d) => {
              const Icon = STATUS_ICON[d.status] || Clock;
              return (
                <tr key={d.id} className={`border-b border-slate-100 hover:bg-slate-50 ${d.status === "pending" ? "bg-amber-50/40" : ""}`} data-testid={`docs-row-${d.id}`}>
                  {isSup && <td className="px-4 py-2 font-medium">{d.user_name}</td>}
                  <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-700 text-white">{d.category}</Badge></td>
                  <td className="px-4 py-2 mono truncate max-w-[280px]">{d.filename}</td>
                  <td className="px-4 py-2">
                    <InlineExpiration doc={d} canEdit={isSup || d.user_id === user?.id} onSaved={(fresh) => setDocs((all) => all.map((x) => x.id === fresh.id ? fresh : x))} />
                  </td>
                  <td className="px-4 py-2 mono text-xs">{new Date(d.uploaded_at).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    <div className="inline-flex items-center gap-1">
                      <Badge className={`rounded-sm border inline-flex items-center gap-1 ${STATUS_STYLE[d.status]}`}>
                        <Icon size={10} weight="fill" />
                        {d.status === "pending" ? "PENDING APPROVAL" : d.status.toUpperCase()}
                      </Badge>
                    </div>
                    {d.status === "approved" && d.approved_by === "auto" && (
                      <div className="text-[10px] text-slate-500 mt-1 mono" data-testid={`docs-autoapproved-${d.id}`}>
                        auto-approved · non-expiring
                      </div>
                    )}
                    {d.status === "approved" && d.approved_by_name && d.approved_by !== "auto" && (
                      <div className="text-[10px] text-slate-500 mt-1 mono" data-testid={`docs-approver-${d.id}`}>
                        by {d.approved_by_name} · {new Date(d.approved_at).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right flex justify-end gap-1">
                    <Button asChild size="sm" variant="outline" className="rounded-sm h-7 text-xs" title="Preview inline" data-testid={`docs-preview-${d.id}`}>
                      <a href={`${API_BASE}/documents/${d.id}/preview`} target="_blank" rel="noreferrer">
                        <Eye size={12} weight="bold" className="mr-1" /> Preview
                      </a>
                    </Button>
                    <Button asChild size="sm" variant="outline" className="rounded-sm h-7 text-xs" title="Download file" data-testid={`docs-download-${d.id}`}>
                      <a href={`${API_BASE}/documents/${d.id}/download`}>
                        <DownloadSimple size={12} weight="bold" className="mr-1" /> Download
                      </a>
                    </Button>
                    {isSup && d.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => review(d, "approved")} data-testid={`docs-approve-${d.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-red-400 text-red-600 hover:bg-red-50" onClick={() => review(d, "rejected")} data-testid={`docs-reject-${d.id}`}>
                          Reject
                        </Button>
                      </>
                    )}
                    {isSup && d.status !== "pending" && (
                      <Button size="sm" variant="ghost" className="rounded-sm h-7 text-xs text-slate-500" onClick={() => review(d, "pending")} data-testid={`docs-reopen-${d.id}`} title="Re-open for review">
                        Re-open
                      </Button>
                    )}
                    {(isSup || d.user_id === user?.id) && (
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(d)} data-testid={`docs-del-${d.id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
